import React, { createContext, useContext, useState, useEffect } from 'react';
import { db } from '../firebaseConfig';
import { collection, query, where, getDocs, setDoc, doc, getDoc } from 'firebase/firestore';
import { getAuth, signInAnonymously, onAuthStateChanged } from 'firebase/auth';

const UserContext = createContext();

export const useUser = () => useContext(UserContext);

export const UserProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const auth = getAuth();

    useEffect(() => {
        // Listen to Firebase Auth state changes
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            if (firebaseUser) {
                // User is signed in, fetch their profile from Firestore
                const userDoc = await getDoc(doc(db, "users", firebaseUser.uid));
                if (userDoc.exists()) {
                    setUser({
                        id: firebaseUser.uid,
                        name: userDoc.data().name
                    });
                } else {
                    // Firebase user exists but no profile in Firestore
                    // This shouldn't happen in normal flow, but handle it
                    setUser(null);
                }
            } else {
                // No user signed in
                setUser(null);
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, [auth]);

    const registerUser = async (name) => {
        // Check if name already exists in Firestore
        const usersRef = collection(db, "users");
        const q = query(usersRef, where("name", "==", name));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
            throw new Error("El nombre de usuario ya está en uso. Por favor elige otro.");
        }

        // Sign in anonymously with Firebase Auth
        const userCredential = await signInAnonymously(auth);
        const firebaseUser = userCredential.user;

        // Save user profile to Firestore using Firebase UID
        await setDoc(doc(db, "users", firebaseUser.uid), {
            name: name,
            createdAt: new Date()
        });

        // Update local state (onAuthStateChanged will also trigger)
        setUser({
            id: firebaseUser.uid,
            name: name
        });
    };

    return (
        <UserContext.Provider value={{ user, registerUser, loading }}>
            {children}
        </UserContext.Provider>
    );
};
